import { create } from 'zustand';
import { queryRecords, createRecord, updateRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from '../auth/auth-store';
import type { Individual } from '../types/intake';
import type { QueryOptions } from '../api/types';

// Stub type — property items not yet implemented in V2
export interface PropertyItem {
  id: number; incidentId: number | null; description: string; category: string;
  status: string; storageLocation: string; quantity: string; ownerId: string;
  itemCode: string; notes: string; photoData?: string; datecreated?: string;
}

const F = FIELDS.individuals;

function mapIndividual(r: Record<string, unknown>): Individual {
  return {
    id: r.record_id as number, incidentId: (r[F.incidentId] as number) || null,
    firstName: (r[F.firstName] as string) || '', lastName: (r[F.lastName] as string) || '',
    dateOfBirth: (r[F.dateOfBirth] as string) || '', gender: (r[F.gender] as string) || '',
    phone: (r[F.phone] as string) || '', email: (r[F.email] as string) || '',
    address: (r[F.address] as string) || '', city: (r[F.city] as string) || '',
    state: (r[F.state] as string) || '', zip: (r[F.zip] as string) || '',
    triage: (r[F.triage] as string) || 'green', status: (r[F.status] as string) || 'active',
    emergencyContactName: (r[F.emergencyContactName] as string) || '',
    emergencyContactPhone: (r[F.emergencyContactPhone] as string) || '',
    medicalNeeds: (r[F.medicalNeeds] as string) || '', registeredBy: (r[F.registeredBy] as string) || '',
    registeredAt: (r[F.registeredAt] as string) || '', dischargedAt: (r[F.dischargedAt] as string) || '',
    notes: (r[F.notes] as string) || '',
  };
}

function toPayload(data: Partial<Individual>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId !== undefined) p[F.incidentId] = data.incidentId;
  if (data.firstName !== undefined) p[F.firstName] = data.firstName;
  if (data.lastName !== undefined) p[F.lastName] = data.lastName;
  if (data.dateOfBirth !== undefined) p[F.dateOfBirth] = data.dateOfBirth;
  if (data.gender !== undefined) p[F.gender] = data.gender;
  if (data.phone !== undefined) p[F.phone] = data.phone;
  if (data.email !== undefined) p[F.email] = data.email;
  if (data.address !== undefined) p[F.address] = data.address;
  if (data.city !== undefined) p[F.city] = data.city;
  if (data.state !== undefined) p[F.state] = data.state;
  if (data.zip !== undefined) p[F.zip] = data.zip;
  if (data.triage !== undefined) p[F.triage] = data.triage;
  if (data.status !== undefined) p[F.status] = data.status;
  if (data.emergencyContactName !== undefined) p[F.emergencyContactName] = data.emergencyContactName;
  if (data.emergencyContactPhone !== undefined) p[F.emergencyContactPhone] = data.emergencyContactPhone;
  if (data.medicalNeeds !== undefined) p[F.medicalNeeds] = data.medicalNeeds;
  if (data.registeredBy !== undefined) p[F.registeredBy] = data.registeredBy;
  if (data.registeredAt !== undefined) p[F.registeredAt] = data.registeredAt;
  if (data.dischargedAt !== undefined) p[F.dischargedAt] = data.dischargedAt;
  if (data.notes !== undefined) p[F.notes] = data.notes;
  return p;
}

interface IntakeState {
  individuals: Individual[]; total: number; loading: boolean; page: number;
  fetch: (opts?: Partial<QueryOptions>) => Promise<void>;
  setPage: (p: number) => void;
  create: (data: Partial<Individual>) => Promise<number | null>;
  update: (id: number, data: Partial<Individual>) => Promise<void>;
  discharge: (id: number) => Promise<void>;
}

export const useIntakeStore = create<IntakeState>((set, get) => ({
  individuals: [], total: 0, loading: false, page: 1,
  fetch: async (opts) => {
    set({ loading: true });
    try {
      const { page } = get();
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: F.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const existingFilters = opts?.filters || [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.individuals, {
        limit: 50, offset: (page - 1) * 50, sort: { field: 'created_at', direction: 'desc' }, ...opts,
        filters: [...incFilter, ...existingFilters],
      });
      set({ individuals: res.records.map(mapIndividual), total: res.total });
    } catch (e) { console.error('[Intake] fetch', e); }
    set({ loading: false });
  },
  setPage: (p) => { set({ page: p }); get().fetch(); },
  create: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.individuals, toPayload(data)); await get().fetch(); return (r as any).record_id; } catch (e) { console.error('[Intake]', e); return null; } },
  update: async (id, data) => { try { await updateRecord(TABLES.individuals, id, toPayload(data)); await get().fetch(); } catch (e) { console.error('[Intake]', e); } },
  discharge: async (id) => { try { await updateRecord(TABLES.individuals, id, { [F.status]: 'discharged', [F.dischargedAt]: new Date().toISOString() }); await get().fetch(); } catch (e) { console.error('[Intake]', e); } },

  // V1 compat aliases
  loadIndividuals: async () => get().fetch(),
  createIndividual: async (data: any) => get().create(data),
  updateIndividual: async (id: any, data: any) => get().update(id, data),
  discharges: [] as any[],
  loadDischarges: async () => {},
  createDischarge: async (_data: any) => 0,
  loading: false,
  addTriageEntry: async (_data: any) => {},
  // Property items — stub until V2 implementation
  propertyItems: [] as any[],
  loadPropertyItems: async (_incidentId?: any) => {},
  createPropertyItem: async (_data: any) => null,
  updatePropertyItem: async (_id: any, _data: any) => {},
  deletePropertyItem: async (_id: any) => {},
}));
